import { z } from 'zod';
import { MOBILE_PATTERN } from '@/lib/forms';

/**
 * The ten digits a typed mobile comes down to, as `normaliseMobile` in the
 * backend — so a number the server would find is never refused here first.
 */
function normaliseMobile(value: string): string {
  return value.replace(/[\s-]/g, '').replace(/^(\+91|0)/, '');
}

/**
 * Mirrors `loginSchema` in the backend's auth.validation.ts.
 *
 * Validating here too means the user sees the mistake instantly instead of
 * after a round trip. The server still validates — client rules are for
 * convenience, never for security.
 *
 * One `identifier` holds either kind, told apart the way the server's login
 * does it: an "@" means an email, anything else is read as a mobile.
 */
export const loginSchema = z
  .object({
    identifier: z
      .string()
      .trim()
      .min(1, 'Please enter your email or mobile number')
      .max(150, 'That is too long'),
    password: z.string().min(1, 'Please enter your password').max(128, 'That is too long'),
    rememberMe: z.boolean().default(false),
  })
  .superRefine(({ identifier }, ctx) => {
    if (identifier === '') return;
    const isEmail = identifier.includes('@');
    const isValid = isEmail
      ? z.string().email().safeParse(identifier).success
      : MOBILE_PATTERN.test(normaliseMobile(identifier));
    if (isValid) return;
    ctx.addIssue({
      code: 'custom',
      path: ['identifier'],
      message: isEmail
        ? 'Enter a valid email address'
        : 'Enter a valid email address or 10-digit mobile number',
    });
  });

export type LoginFormValues = z.infer<typeof loginSchema>;
